// src/components/admin/LoyaltyAI.jsx - FIXED VERSION with proper redirect
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { LoyaltyAIService } from '../../api/backendAiService';
import DailyTokenCounter from './DailyTokenCounter';
import { useNavigate, useParams } from 'react-router-dom';

function LoyaltyAI({ salonId: propSalonId }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [salonContext, setSalonContext] = useState({});
  const [showFeedback, setShowFeedback] = useState({});
  
  const [limitReached, setLimitReached] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const aiServiceRef = useRef(null);
  const tokenCounterRef = useRef(null);

  const navigate = useNavigate();
  const params = useParams();
  
  // Get salonId from props or URL params
  const salonId = propSalonId || params.salonId;

  // Initialize AI service when salonId is available
  useEffect(() => {
    if (salonId && !aiServiceRef.current) {
      console.log('Initializing AI service for salonId:', salonId);
      aiServiceRef.current = new LoyaltyAIService(salonId);
    }
  }, [salonId]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputMessage(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  // Load salon context on component mount
  useEffect(() => {
    const loadContext = async () => {
      if (!aiServiceRef.current) {
        console.log('AI service not initialized yet');
        return;
      }

      try {
        console.log('Loading salon context...');
        const context = await aiServiceRef.current.getSalonContext();
        setSalonContext(context);
        
        console.log('Salon context loaded:', context);
        
        // Add initial AI message
        const initialMessage = {
          id: Date.now(),
          type: 'ai',
          content: `Hello! I'm your Bespoke AI assistant for promotional campaigns. I can help you create targeted promotions to drive bookings, voucher redemptions, and customer spending.

I have access to your salon's data:
• ${context.customers?.length || 0} customer profiles
• ${context.pastPromotions?.length || 0} past promotions
• ${context.bookings?.length || 0} recent bookings
• ${context.services?.length || 0} services

What type of promotion would you like to create today? I can help you optimize for maximum impact based on your salon's performance data.`,
          timestamp: new Date()
        };
        
        setMessages([initialMessage]);
      } catch (error) {
        console.error('Error loading salon context:', error);
        
        // Add error message
        const errorMessage = {
          id: Date.now(),
          type: 'system',
          content: 'I had trouble loading your salon data. I can still help you create promotions, but my recommendations may be less personalized. What would you like to create?',
          timestamp: new Date()
        };
        setMessages([errorMessage]);
      }
    };

    if (salonId && aiServiceRef.current) {
      loadContext();
    }
  }, [salonId]);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle voice input
  const handleVoiceInput = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  // Handle sending message
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !aiServiceRef.current || limitReached) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Send to AI service
      const response = await aiServiceRef.current.sendMessage(inputMessage, salonContext);
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.message,
        timestamp: new Date(),
        parameters: response.parameters
      };

      setMessages(prev => [...prev, aiMessage]);
      setShowFeedback(prev => ({ ...prev, [aiMessage.id]: true }));
      if (response.dailyUsage) tokenCounterRef.current?.update(response.dailyUsage);

      // Check if we should redirect to promotions
      if (response.shouldRedirect && response.parameters) {
        console.log('Redirecting with parameters:', response.parameters);
        
        // Add system message
        const redirectMessage = {
          id: Date.now() + 2,
          type: 'system',
          content: 'Redirecting you to the Promotions module...',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, redirectMessage]);
        
        setTimeout(() => {
          handleRedirectToPromotions(response.parameters);
        }, 1500);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      const isLimitError = error?.status === 429 || error?.message?.includes('daily AI usage limit');
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: isLimitError
          ? 'You have exceeded your daily AI usage limit of 10,000 tokens. AI is unavailable until 00:00:01.'
          : "I'm experiencing technical difficulties. Please try again or contact support if the issue persists.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      tokenCounterRef.current?.refresh();
    }
  };

  // Handle feedback
  const handleFeedback = async (messageId, feedback) => {
    try {
      await aiServiceRef.current.storeFeedback(messageId, feedback);
      setShowFeedback(prev => ({ ...prev, [messageId]: false }));
      
      // Show confirmation
      const confirmationMessage = {
        id: Date.now(),
        type: 'system',
        content: `Thank you for your feedback! This helps me improve my recommendations.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, confirmationMessage]);
    } catch (error) {
      console.error('Error storing feedback:', error);
    }
  };

  // FIXED: Handle redirect to promotions with parameters
  const handleRedirectToPromotions = (parameters) => {
    console.log('Storing parameters in sessionStorage:', parameters);

    // Auto-generate description if missing
    if (!parameters.description && aiServiceRef.current) {
      parameters.description = aiServiceRef.current.generateDescription();
      console.log('Auto-generated description:', parameters.description);
    }

    // Ensure targetingCriteria has proper structure
    if (!parameters.targetingCriteria || typeof parameters.targetingCriteria === 'string') {
      parameters.targetingCriteria = {
        birthMonths: [],
        postalCodes: [],
        lastTransactionMonths: 0,
        transactionCount: 0,
        transactionType: 'not_selected',
        transactionAmountType: 'not_selected',
        transactionAmount: 0,
        memberTiers: [],
        pointsType: 'not_selected',
        pointsAmount: 0,
        specificCustomers: []
      };
    }

    // Store parameters in sessionStorage for the promotions form
    sessionStorage.setItem('aiPromotionParameters', JSON.stringify(parameters));
    sessionStorage.setItem('aiPromotionSource', 'bespokeai');
    sessionStorage.setItem('targetTab', 'promotions'); // FIXED: Use correct tab name

    // Navigate to promotions module using correct route structure
    const promotionsPath = `/BespokeBeauty/${salonId}/admin/promotions`;
    console.log('Navigating to:', promotionsPath);

    // Use navigate with state to force a route change
    navigate(promotionsPath, { replace: false, state: { fromAI: true, timestamp: Date.now() } });
  };

  // Handle example prompt click
  const handleExampleClick = (prompt) => {
    setInputMessage(prompt);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="ai-tab-container">
      <div className="ai-tab-header">
        <h2>Loyalty AI</h2>
        <p>Get AI-powered recommendations for creating effective promotional campaigns</p>
      </div>
      
      <DailyTokenCounter
        ref={tokenCounterRef}
        salonId={propSalonId}
        onLimitReached={setLimitReached}
      />

      <div className="ai-interaction-panel">
        <div className="ai-prompt-examples">
          <h3>Try asking me:</h3>
          <div className="example-prompts">
            <div 
              className="prompt-card"
              onClick={() => handleExampleClick("Help me create a Christmas promotion campaign that will drive the highest conversions based on past campaign results")}
            >
              "Help me create a Christmas promotion campaign that will drive the highest conversions based on past campaign results"
            </div>
            <div 
              className="prompt-card"
              onClick={() => handleExampleClick("Create a birthday promotion campaign for customers born in December")}
            >
              "Create a birthday promotion campaign for customers born in December"
            </div>
            <div 
              className="prompt-card"
              onClick={() => handleExampleClick("Analyze my previous promotions and suggest improvements for better engagement")}
            >
              "Analyze my previous promotions and suggest improvements for better engagement"
            </div>
            <div 
              className="prompt-card"
              onClick={() => handleExampleClick("What's the best promotion type to increase customer visit frequency?")}
            >
              "What's the best promotion type to increase customer visit frequency?"
            </div>
          </div>
        </div>
        
        <div className="ai-chat-interface">
          <div className="chat-messages">
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.type}-message`}>
                {message.type === 'ai' && (
                  <div className="ai-avatar">🤖</div>
                )}
                {message.type === 'user' && (
                  <div className="user-avatar">👤</div>
                )}
                {message.type === 'system' && (
                  <div className="system-avatar">ℹ️</div>
                )}
                <div className="message-content">
                  <div className="message-text"><ReactMarkdown>{message.content}</ReactMarkdown></div>
                  <div className="message-time">{formatTime(message.timestamp)}</div>
                  
                  {/* Show parameters if collected */}
                  {message.parameters && Object.keys(message.parameters).length > 0 && (
                    <div className="collected-parameters">
                      <h4>Collected Parameters:</h4>
                      <ul>
                        {Object.entries(message.parameters).map(([key, value]) => (
                          <li key={key}>
                            <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : value}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Feedback buttons for AI messages */}
                  {message.type === 'ai' && showFeedback[message.id] && (
                    <div className="message-feedback">
                      <span>Was this helpful?</span>
                      <button 
                        className="feedback-btn positive"
                        onClick={() => handleFeedback(message.id, 'positive')}
                      >
                        👍
                      </button>
                      <button 
                        className="feedback-btn negative"
                        onClick={() => handleFeedback(message.id, 'negative')}
                      >
                        👎
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="message ai-message">
                <div className="ai-avatar">🤖</div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          <div className="chat-input-area">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={limitReached ? 'Daily AI usage limit reached. Resets at 00:00:01.' : 'Type your message here or click examples above... (Press Enter to send, Shift+Enter for new line)'}
              rows="3"
              disabled={isLoading || limitReached}
            />
            <div className="input-actions">
              <button
                className={`voice-input-btn ${isListening ? 'listening' : ''}`}
                onClick={handleVoiceInput}
                disabled={isLoading || limitReached}
              >
                {isListening ? '⏹️ Stop' : '🎤 Voice Input'}
              </button>
              <button
                className="send-btn"
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim() || limitReached}
              >
                {isLoading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="ai-features-info">
        <h3>How to Use Loyalty AI:</h3>
        <div className="feature-list">
          <div className="feature-item">
            <strong>1. Ask for Recommendations:</strong> Describe what you want to achieve
          </div>
          <div className="feature-item">
            <strong>2. Provide Details:</strong> I'll guide you through all promotion parameters
          </div>
          <div className="feature-item">
            <strong>3. Say "CONFIRMED":</strong> When ready, I'll populate the promotion form for you
          </div>
          <div className="feature-item">
            <strong>4. Give Feedback:</strong> Help me learn by rating my suggestions
          </div>
        </div>
        
        <div className="ai-capabilities">
          <h4>AI Capabilities:</h4>
          <ul>
            <li>✅ Campaign performance analysis</li>
            <li>✅ Customer segmentation insights</li>
            <li>✅ Optimal timing recommendations</li>
            <li>✅ ROI optimization strategies</li>
            <li>✅ Voice and text interaction</li>
            <li>✅ Learning from feedback</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default LoyaltyAI;